import { dayjs } from '@tunasong/models'
import type { AccessRequestView, ApprovablePermission } from '@tunasong/schemas'
import {
  Button,
  EntityIcon,
  fillCopy,
  requestAccessCopy as copy,
  Typography,
  useNavigate,
  UserAvatar,
} from '@tunasong/ui-lib'
import type { FC, MouseEvent } from 'react'
import { ApproveSplitButton } from './approve-split-button.js'
import type { AccessRequestActions } from './use-access-request-actions.js'
import styles from './access-request-row.module.css'

/** Joni's homeRow sentence stays the single source; the title is spliced in as a link (§8.5, §8.7) */
const [beforeTitle = '', afterTitle = ''] = copy.homeRow.split('{title}')

export interface AccessRequestRowProps {
  view: AccessRequestView
  /** dialog: name, email and time; home: "{name} asked for access to {title}" with the title linked (§8.4, §8.5) */
  layout: 'dialog' | 'home'
  actions: AccessRequestActions
  /** The row has left the list (success, 409, 404): the host moves focus */
  onSettled(): void
}

/** One pending access request with its Approve split button and Decline. Shared by the Share dialog and Home. */
export const AccessRequestRow: FC<AccessRequestRowProps> = props => {
  const { view, layout, actions, onSettled } = props
  const navigate = useNavigate()
  const name = view.requester.name ?? view.requester.email
  const title = view.entity.name ?? 'Untitled'
  const when = dayjs(view.createdAt).fromNow()
  const itemPath = `/app/entities/${view.entity.id}`

  const handleOpen = (ev: MouseEvent) => {
    ev.preventDefault()
    void navigate(itemPath)
  }
  const handleApprove = async (permission: ApprovablePermission) => {
    if (await actions.approve(view, permission)) {
      onSettled()
    }
  }
  const handleDecline = async () => {
    if (await actions.decline(view)) {
      onSettled()
    }
  }

  return (
    <li className={styles.row} data-layout={layout}>
      <div className={styles.who}>
        <UserAvatar userId={view.requester.userId} aria-hidden />
        {layout === 'dialog' ? (
          <div className={styles.text}>
            <Typography variant="body2">{name}</Typography>
            <Typography variant="caption" className={styles.meta}>
              {view.requester.email}
            </Typography>
            <Typography variant="caption" className={styles.meta}>
              {when}
            </Typography>
          </div>
        ) : (
          <div className={styles.text}>
            <Typography variant="body2" className={styles.sentence}>
              {fillCopy(beforeTitle, { name })}
              <span className={styles.icon} aria-hidden>
                <EntityIcon entity={{ type: view.entity.type } as never} size="tiny" />
              </span>
              <a href={itemPath} onClick={handleOpen}>
                {title}
              </a>
              {fillCopy(afterTitle, { name })}
            </Typography>
            <Typography variant="caption" className={styles.meta}>
              {when}
            </Typography>
          </div>
        )}
      </div>
      <div className={styles.actions}>
        <ApproveSplitButton
          name={name}
          disabled={actions.busy}
          onApprove={permission => void handleApprove(permission)}
        />
        <Button
          size="small"
          className={styles.action}
          disabled={actions.busy}
          aria-label={`${copy.decline} ${name}`}
          onClick={() => void handleDecline()}
        >
          {copy.decline}
        </Button>
      </div>
    </li>
  )
}
